import { useRouter } from "next/router";
import { type PropsWithChildren, useEffect, useState } from "react";

import { LoadingState } from "@/components/shared/layout/LoadingState";
import { RouteConfig } from "@/config/route.config";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import type { ProfileModels } from "@/data/profile/profile.models";

interface IProps {
  type: "public-only" | "private";
  redirectTo?: string;
  /**
   * If true, automatically checks if user is in demo mode (redirects to demo)
   * or if user hasn't finished onboarding (redirects to onboarding).
   * Default: false
   */
  requireOnboarding?: boolean;
  additionalCheck?: (user: ProfileModels.FullProfileResponseDto) => boolean;
  additionalCheckRedirectTo?: string;
}

export const AuthGuard = ({
  type,
  redirectTo,
  children,
  requireOnboarding,
  additionalCheck,
  additionalCheckRedirectTo,
}: PropsWithChildren<IProps>) => {
  const { user, isInitializing, isLoggedIn } = AuthContext.useAuth();
  const router = useRouter();

  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted || isInitializing) {
    return <LoadingState />;
  }

  // `/postsso` is a special landing page after OAuth/SSO flows.
  // It must not be auto-redirected by global auth/onboarding logic.
  if (router.pathname === "/postsso") {
    return children;
  }

  // 1. Primary Authentication Checks (must happen first)
  if (type === "private" && !isLoggedIn && !user && hasMounted && !isInitializing) {
    router.replace(RouteConfig.Signin);
    return null;
  }

  if (type === "private" && !user) {
    router.replace(redirectTo || RouteConfig.Home);
    return null;
  }

  if (type === "public-only" && user) {
    router.replace(redirectTo || RouteConfig.Home);
    return null;
  }

  // 2. Custom additional checks (legacy/specific)
  if (additionalCheck && user && additionalCheck(user)) {
    router.replace(additionalCheckRedirectTo || RouteConfig.Home);
    return null;
  }

  // 3. Standard Onboarding Checks
  if (requireOnboarding && user) {
    // Check Onboarding Status
    const isOnboarded = user.onboardingStep === CommonModels.OnboardingStepEnum.completed;

    if (!isOnboarded) {
      router.replace(RouteConfig.Onboarding);
      return null;
    }
  }

  return children;
};
